import { createContext, useContext, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Phone } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    API,
    CITY_OPTIONS,
    INPUT_CLASS,
    PHONE_DISPLAY,
    SERVICE_OPTIONS,
    TEL_URL,
    buildWaQuoteUrl,
} from "@/lib/site";

const QuoteContext = createContext(() => {});
export const useQuote = () => useContext(QuoteContext);

const EMPTY = { name: "", phone: "", city: "", service: "", message: "" };

const LABEL_CLASS =
    "mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600";

export const QuoteProvider = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});

    const update = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const submit = (e) => {
        e.preventDefault();
        const errs = {};
        if (form.name.trim().length < 2) errs.name = "Please enter your name.";
        if (!/^[0-9+\-\s()]{8,20}$/.test(form.phone.trim()))
            errs.phone = "Please enter a valid phone number.";
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;

        axios
            .post(`${API}/enquiries`, {
                full_name: form.name.trim(),
                phone: form.phone.trim(),
                city: form.city || "Not specified",
                service_required: form.service || "Not specified",
                message: form.message.trim() || null,
            })
            .catch(() => {});

        window.open(
            buildWaQuoteUrl({
                name: form.name.trim(),
                phone: form.phone.trim(),
                city: form.city,
                service: form.service,
                message: form.message.trim(),
            }),
            "_blank",
            "noopener",
        );
        toast.success("Opening WhatsApp with your details pre-typed.");
        setForm(EMPTY);
        setErrors({});
        setOpen(false);
    };

    return (
        <QuoteContext.Provider value={() => setOpen(true)}>
            {children}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    data-testid="quote-modal"
                    className="border-slate-200 bg-white sm:max-w-md"
                >
                    <DialogHeader>
                        <DialogTitle className="text-xl font-extrabold tracking-tight text-[#0B1528]">
                            GET A FREE QUOTE
                        </DialogTitle>
                        <DialogDescription className="text-sm text-slate-500">
                            Share your details — WhatsApp will open with everything pre-typed,
                            ready to send to K1 ELEVATOR&rsquo;S.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} noValidate className="mt-2 space-y-4">
                        <div>
                            <label htmlFor="quote-name" className={LABEL_CLASS}>
                                Your Name *
                            </label>
                            <input
                                id="quote-name"
                                data-testid="quote-modal-name"
                                type="text"
                                value={form.name}
                                onChange={update("name")}
                                placeholder="Full name"
                                className={INPUT_CLASS}
                            />
                            {errors.name && (
                                <p
                                    data-testid="quote-modal-name-error"
                                    className="mt-1.5 text-xs font-semibold text-[#9F1239]"
                                >
                                    {errors.name}
                                </p>
                            )}
                        </div>
                        <div>
                            <label htmlFor="quote-phone" className={LABEL_CLASS}>
                                Phone Number *
                            </label>
                            <input
                                id="quote-phone"
                                data-testid="quote-modal-phone"
                                type="tel"
                                value={form.phone}
                                onChange={update("phone")}
                                placeholder="+91 XXXXX XXXXX"
                                className={INPUT_CLASS}
                            />
                            {errors.phone && (
                                <p
                                    data-testid="quote-modal-phone-error"
                                    className="mt-1.5 text-xs font-semibold text-[#9F1239]"
                                >
                                    {errors.phone}
                                </p>
                            )}
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label htmlFor="quote-city" className={LABEL_CLASS}>
                                    City
                                </label>
                                <select
                                    id="quote-city"
                                    data-testid="quote-modal-city"
                                    value={form.city}
                                    onChange={update("city")}
                                    className={INPUT_CLASS}
                                >
                                    <option value="">Select city</option>
                                    {CITY_OPTIONS.map((c) => (
                                        <option key={c} value={c}>
                                            {c}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="quote-service" className={LABEL_CLASS}>
                                    Service
                                </label>
                                <select
                                    id="quote-service"
                                    data-testid="quote-modal-service"
                                    value={form.service}
                                    onChange={update("service")}
                                    className={INPUT_CLASS}
                                >
                                    <option value="">Select service</option>
                                    {SERVICE_OPTIONS.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label htmlFor="quote-message" className={LABEL_CLASS}>
                                Message
                            </label>
                            <textarea
                                id="quote-message"
                                data-testid="quote-modal-message"
                                rows={2}
                                value={form.message}
                                onChange={update("message")}
                                placeholder="Briefly describe your requirement..."
                                className={`${INPUT_CLASS} resize-y`}
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="quote-modal-submit-btn"
                            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] px-6 py-3.5 text-sm font-bold text-white shadow-[0_4px_16px_rgba(37,99,235,0.4)] transition-[background-color,transform] duration-200 hover:bg-[#1D4ED8] active:scale-[0.98]"
                        >
                            <MessageCircle className="h-4 w-4" /> SEND ON WHATSAPP
                        </button>
                        <a
                            href={TEL_URL}
                            data-testid="quote-modal-call-link"
                            className="flex items-center justify-center gap-2 text-sm font-bold text-[#1D4ED8] transition-colors duration-200 hover:text-[#1E40AF]"
                        >
                            <Phone className="h-4 w-4" /> Prefer to talk? Call {PHONE_DISPLAY}
                        </a>
                    </form>
                </DialogContent>
            </Dialog>
        </QuoteContext.Provider>
    );
};
